import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import Navbar from './components/Navbar';
import HomePage from './pages/HomePage';
import AuthPage from './pages/AuthPage';
import SearchPage from './pages/SearchPage';
import ListingDetailPage from './pages/ListingDetailPage';
import HostDashboard from './pages/HostDashboard';
import GuestBookings from './pages/GuestBookings';
import GuestMessagesPage from './pages/GuestMessagesPage';
import AdminDashboard from './pages/AdminDashboard';
import VerificationPage from './pages/VerificationPage';
import ReferralDashboard from './pages/ReferralDashboard';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import BecomeHostPage from './pages/BecomeHostPage';
import AffiliateLandingPage from './pages/AffiliateLandingPage';
import AffiliateApplyPage from './pages/AffiliateApplyPage';
import AffiliateLoginPage from './pages/AffiliateLoginPage';
import AffiliateDashboardPage from './pages/AffiliateDashboardPage';
import AdminAffiliatesPage from './pages/AdminAffiliatesPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import CancellationPolicyPage from './pages/CancellationPolicyPage';
import HostAgreementPage from './pages/HostAgreementPage';
import HostLandingPage from './pages/HostLandingPage';
import GuestLandingPage from './pages/GuestLandingPage';
import './App.css';

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
}

function ProtectedRoute({ children, requireHost, requireAdmin }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-zinc-400">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" />;
  }

  // Check admin requirement
  if (requireAdmin && user.role !== 'admin') {
    return <Navigate to="/" />;
  }

  // Check host requirement - user must be a host or admin
  if (requireHost && !user.is_host && user.role !== 'admin') {
    return <Navigate to="/become-host" />;
  }

  return children;
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <ScrollToTop />
        <div className="min-h-screen bg-[#050505] font-sans">
          <Navbar />
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/search" element={<SearchPage />} />
            <Route path="/listing/:id" element={<ListingDetailPage />} />
            
            <Route
              path="/verification"
              element={
                <ProtectedRoute>
                  <VerificationPage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/referrals"
              element={
                <ProtectedRoute>
                  <ReferralDashboard />
                </ProtectedRoute>
              }
            />

            {/* Become a Host - anyone logged in can access */}
            <Route
              path="/become-host"
              element={
                <ProtectedRoute>
                  <BecomeHostPage />
                </ProtectedRoute>
              }
            />
            
            {/* Host Dashboard - requires host status */}
            <Route
              path="/host/dashboard"
              element={
                <ProtectedRoute requireHost>
                  <HostDashboard />
                </ProtectedRoute>
              }
            />
            
            {/* Guest Bookings - any logged in user can view their bookings */}
            <Route
              path="/guest/bookings"
              element={
                <ProtectedRoute>
                  <GuestBookings />
                </ProtectedRoute>
              }
            />
            
            {/* Guest Messages - any logged in user can view their messages */}
            <Route
              path="/guest/messages"
              element={
                <ProtectedRoute>
                  <GuestMessagesPage />
                </ProtectedRoute>
              }
            />
            
            {/* Admin Dashboard */}
            <Route
              path="/admin/dashboard"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            
            {/* Admin Affiliates Management */}
            <Route
              path="/admin/affiliates"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminAffiliatesPage />
                </ProtectedRoute>
              }
            />
            
            {/* Affiliate Program Routes (Public) */}
            <Route path="/affiliate" element={<AffiliateLandingPage />} />
            <Route path="/affiliate/apply" element={<AffiliateApplyPage />} />
            <Route path="/affiliate/login" element={<AffiliateLoginPage />} />
            <Route path="/affiliate/dashboard" element={<AffiliateDashboardPage />} />
            
            {/* Marketing Landing Pages */}
            <Route path="/host" element={<HostLandingPage />} />
            <Route path="/guest" element={<GuestLandingPage />} />
            
            {/* Legal Pages */}
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsOfServicePage />} />
            <Route path="/cancellation-policy" element={<CancellationPolicyPage />} />
            <Route path="/host-agreement" element={<HostAgreementPage />} />
          </Routes>
          <Toaster position="top-right" richColors />
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;